import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { observable } from 'mobx'
import { unixEpoch } from '@/utils/date-util'
import type { TaxRefundDeliveryMethod } from './TaxRefundDeliveryMethod'
import { match } from '@/utils/pattern-match-util'

/**
 * Whether the refund is for a personal or business taxpayer.
 */
export type TaxRefundType = 'Personal' | 'Business'

/**
 * Props for {@link TaxRefundMethodVersion}.
 */
export type TaxRefundMethodVersionProps = ModelProps<TaxRefundMethodVersion>

/**
 * A means by which a tax refund can be delivered.
 */
export class TaxRefundMethodVersion extends Model<TaxRefundMethodVersion> {
  id = Id.none

  @observable accessor version = 0
  @observable accessor workspaceId = Id.none
  @observable accessor ownerUserId = Id.none
  @observable accessor refundType: TaxRefundType = 'Personal'
  @observable accessor deliveryMethod: TaxRefundDeliveryMethod = {
    type: 'Check',
    mailingAddress: null,
  }
  @observable accessor createdAt = unixEpoch()
  @observable accessor updatedAt = unixEpoch()

  /**
   * Whether the method version has the given identity.
   */
  equalsIdentity(id: Id, version: number | undefined) {
    return this.id === id && this.version === version
  }
}

export const TaxRefundMethodVersionId = {
  from: (id: Id, version: number | undefined): string => {
    if (version === undefined) {
      throw new TypeError('Tax refund method version ID cannot be undefined')
    }
    return `${Id.assert(id)}@v${version}`
  },
}

/**
 * Formats a tax refund method into a readable description.
 */
export function formatRefundMethodDescription(
  method: TaxRefundMethodVersion,
): string {
  if (method.deliveryMethod.type === 'FutureTaxCredit') {
    return 'future tax credit'
  }

  const refund = match(method.refundType, {
    Personal: 'personal tax',
    Business: 'business tax',
  })

  const delivery = match(method.deliveryMethod.type, {
    Check: 'paper check',
    DirectDeposit: 'direct deposit',
  })

  return `${refund} ${delivery}`
}
